import React, { useCallback, useMemo } from 'react';
import { FieldValues, Path, useFormContext } from 'react-hook-form';
import { NumericFormField } from '@components/atoms/form/fields/numeric-form-field';
import ClipboardButtons from '@components/atoms/clipboard-buttons/clipboard-buttons';
import { FormSection } from '@components/organisms/scenarios-simulator/SimulatorForm/presentational';
import useSectionClipboard from '@hooks/use-section-clipboard';
import { ISimulationPayload } from '@schemas/scenarios-simulator/types';
import { ScenarioBasePath } from '@hooks/scenarios-simulator/types';
import { EditableToggleSection } from '@components/molecules/form/simulator/sections/EditableToggleSection';
import { customHistoryFlagOf } from '@hooks/scenarios-simulator/business/utils/sync-target-consumption-history';
import { HistoryKeys } from '.';

const monthsIndexer: Record<number, string> = {
  1: 'Janeiro',
  2: 'Fevereiro',
  3: 'Março',
  4: 'Abril',
  5: 'Maio',
  6: 'Junho',
  7: 'Julho',
  8: 'Agosto',
  9: 'Setembro',
  10: 'Outubro',
  11: 'Novembro',
  12: 'Dezembro',
};

function HistorySection<T extends FieldValues>({
  title,
  subtitle,
  sectionTitle,
  activeClipboard,
  tooltipContent,
  field,
  scenarioBasePath,
  unitNum,
  historyKey,
}: {
  unitNum: number;
  scenarioBasePath: ScenarioBasePath;
  sectionTitle: string;
  title?: string;
  subtitle?: string;
  activeClipboard?: boolean;
  tooltipContent?: string;
  field?: Path<T>;
  historyKey: HistoryKeys;
}) {
  const { getValues, setValue } = useFormContext();
  const monthFields = Object.keys(monthsIndexer).map((n) => `month${n}`);
  const isTargetScenarios = scenarioBasePath.includes('targetScenarios');
  const customHistoryPath = [
    String(scenarioBasePath),
    'scenarioPayload',
    String(unitNum),
    'presetPayload',
    customHistoryFlagOf(historyKey),
  ].join('.');

  const markHistoryAsCustom = useCallback(() => {
    if (!isTargetScenarios || getValues(customHistoryPath)) return;
    setValue(customHistoryPath, true, { shouldDirty: true, shouldValidate: false });
  }, [customHistoryPath, getValues, isTargetScenarios, setValue]);
  const relFields = monthFields.map((m) => `${m}`);
  const basePath = String(field || '');
  const { handleCopy, handlePaste } = useSectionClipboard({
    basePath,
    fieldsToCopy: relFields,
    label: title || 'Histórico',
  });

  const formatProps = useMemo(
    () => ({
      mask: '_',
      allowNegative: false,
      fixedDecimalScale: false,
      decimalSeparator: ',',
      thousandSeparator: '.',
      decimalScale: 10,
      placeholder: 'kWh ',
      suffix: ' kWh',
    }),
    [],
  );

  const errorFields = Object.keys(monthsIndexer).map((n) => `${field}.month${n}`);

  const fieldsContent = (
    <EditableToggleSection
      title={title as string}
      subtitle={'Faça a edição caso o consumo sejam diferentes do Cenário Atual'}
      isTargetScenario={isTargetScenarios}
      scenarioBasePath={scenarioBasePath}
      unitNum={unitNum}
      keys={historyKey}
      errorFields={errorFields}
    >
      <div className="w-full" onInput={markHistoryAsCustom} onPaste={markHistoryAsCustom}>
        <FormSection.Fields className="w-full" gridColumn={6}>
          {Object.entries(monthsIndexer).map(([monthNumber, monthLabel]) => {
            const monthKey = `month${monthNumber}`;

            return (
              <NumericFormField<ISimulationPayload>
                key={`${field}-field-${monthKey}`}
                field={`${field}.${monthKey}` as Path<ISimulationPayload>}
                id={`${field}.${monthKey}`}
                label={monthLabel}
                formatProps={formatProps}
                rootStyle="col-span-1 min-w-full"
                options={{ required: true }}
              />
            );
          })}
        </FormSection.Fields>
      </div>
    </EditableToggleSection>
  );

  return (
    <FormSection.Container className="flex gap-10">
      <FormSection.Header title={sectionTitle ?? ''} subtitle="" tooltipContent={tooltipContent}>
        {!isTargetScenarios && activeClipboard && (
          <ClipboardButtons
            containerClassName="grid grid-cols-2 gap-2 items-center justify-start"
            copy={{ className: 'col-span-1', icon: 'Square2StackIcon', text: 'Copiar inputs', onClick: handleCopy }}
            paste={{
              className: 'col-span-1',
              icon: 'ClipboardDocumentCheckIcon',
              text: 'Colar inputs',
              onClick: handlePaste,
            }}
          />
        )}
      </FormSection.Header>
      {fieldsContent}
    </FormSection.Container>
  );
}

export default React.memo(HistorySection) as typeof HistorySection;
